import FlyoutModal from "@teamscheme/flyout-modal";
import { InfoButtonProps } from "@teamscheme/info-button";
import { ReactNode, useCallback, useContext } from "react";
import { createContext, useState } from "react";
import styled, { css } from "styled-components";
import StyledInfoButton from "./InfoButton";

export const InputDescriptionStyling = css`
  color: ${({ theme }) => theme.nafColor.signature.black};

  > p,
  > ul {
    line-height: 1.5;
    margin-top: 0;
    font-weight: lighter;
    letter-spacing: 0px;
  }
`;

const Description = styled.div`
  ${InputDescriptionStyling}
  margin-bottom: 10px;
`;

export interface IInputDescriptionModalContext {
  open(content: ReactNode): void;
}

export const InputDescriptionModalContext =
  createContext<IInputDescriptionModalContext | null>(null);

export function InputDescriptionModalProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState<ReactNode>(<></>);

  const open = useCallback(function (content: ReactNode) {
    setContent(content);
    setIsOpen(true);
  }, []);

  return (
    <>
      <InputDescriptionModalContext.Provider value={{ open }}>
        {children}
      </InputDescriptionModalContext.Provider>
      <FlyoutModal isOpen={isOpen} onClose={() => setIsOpen(false)}>
        {content}
      </FlyoutModal>
    </>
  );
}

export interface InputDescriptionProps {
  description: string;
}

export default function InputDescription({
  description,
}: InputDescriptionProps) {
  if (!description) return null;
  return <Description dangerouslySetInnerHTML={{ __html: description }} />;
}

export interface DescriptionInfoButtonProps
  extends InputDescriptionProps,
    Omit<InfoButtonProps, "type" | "onClick"> {
  label?: ReactNode;
}

export function DescriptionInfoButton({
  label,
  description,
  ...props
}: DescriptionInfoButtonProps) {
  const context = useContext(InputDescriptionModalContext);
  if (!context) throw new Error("No InputDescriptionModalContext found!");
  const content = (
    <>
      {label ? <h3>{label}</h3> : null}
      <InputDescription description={description} />
    </>
  );
  return (
    <StyledInfoButton
      {...props}
      type="button"
      onClick={(e) => {
        e.preventDefault();
        context.open(content);
      }}
    />
  );
}
